import {ComponentEntity, ResourceEntity, stringifyEntityRef} from '@backstage/catalog-model';
import { Box, Button, Card, CardContent, Chip, CircularProgress, createStyles, Divider, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText, ListSubheader, makeStyles, Tab, Tabs, TextField, Theme, Typography } from '@material-ui/core';
import CodeIcon from '@material-ui/icons/Code';
import React, { useState } from 'react';
import { AngularIcon, GCPComputeEngineIcon, GCPDatabaseIcon, GCPSecretManagerIcon, GCPStorageIcon, GoogleMapsIcon, IonicIcon, SpringBootIcon } from './../icons';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { DevopsComponentLinks } from './devops/DevopsComponentLinks';
import { SonarComponentLinks } from './sonarqube/SonarComponentLinks';
import useAsync from 'react-use/esm/useAsync';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { MessagingResource } from '@internal/open-catalog';
import { useNavigate } from 'react-router';
import { TotvsLogoIcon } from '../../themes';
import { ResourceType } from '../../pages/request/types';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { requestManagerCreatePermission } from '@internal/backstage-plugin-request-manager-common';
import { AddonsTab } from '../addons/AddonsTab';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        divider: { marginTop: '1em' },
        root: {
            gap: theme.spacing(1),
            alignItems: 'center'
        },
        deprecatedChip: {
            margin: 0,
            marginLeft: '1em',
            letterSpacing: 0.75,
            background: theme.palette.errorBackground
        },
        productionChip: {
            margin: 0,
            marginLeft: '1em',
            letterSpacing: 0.75,
        },
        schedulerChip: {
            margin: 0,
            marginLeft: '1em',
            letterSpacing: 0.75,
            background: theme.palette.warningBackground
        },
        ul: {
            padding: 0
        },
        groupName: {
            textTransform: 'uppercase',
            letterSpacing: 0.75
        },
        tab: {
            padding: '20px 0',
            color: theme.palette.grey[500],
            '&:hover': {
                textDecoration: 'underline',
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.default,
            },
        },
        selectedTab: {
            fontWeight: 700,
        },
    })
);

function getResourceIcon(type: string): React.ReactNode {
    switch (type) {
        case 'gcp-sql-instance':
            return <GCPComputeEngineIcon />;
        case 'gcp-sql-database':
            return <GCPDatabaseIcon />;
        case 'gcp-maps-api':
            return <GoogleMapsIcon />;
        case 'gcp-storage-bucket':
            return <GCPStorageIcon />;
        case 'gcp-secret-manager':
            return <GCPSecretManagerIcon />;
        case 'totvs-rac':
        case 'totvs-identity':
        case 'totvs-smart-talk':
        case 'totvs-tpd':
            return <TotvsLogoIcon/>;
        default:
            return <CodeIcon />;
    }
}

const ResourceGridItem = ({ resource }: { resource: ResourceEntity }) => {
    const { title, name, description } = resource.metadata;
    const { type } = resource.spec;

    const classes = useStyles();

    return (<ListItem className={classes.root}>
        <ListItemIcon>{getResourceIcon(type)}</ListItemIcon>
        <ListItemText secondary={description}>
            <Typography variant='body2'>
                {title ? title : name}
                <Chip component='span' label={type} size='small' className={classes.productionChip} />
            </Typography>
        </ListItemText>
    </ListItem>);
}

function getComponentIcon(type: string): React.ReactNode {
    if (type === 'service') {
        return <SpringBootIcon />;
    } else if (type === 'website') {
        return <AngularIcon />;
    } else if (type === 'app') {
        return <IonicIcon />;
    }
    return <CodeIcon />
}

const ComponentGridItem = (props: { component: ComponentEntity }) => {
    const { component } = props;
    const { title, name, description } = component.metadata;
    const { type, lifecycle } = component.spec;

    const catalogApi = useApi(catalogApiRef);
    const classes = useStyles();

    const { value: resources } = useAsync(async () => {
        const response = await catalogApi.getEntities({
            filter: {
                kind: 'Resource',
                ['relations.dependencyOf']: [stringifyEntityRef(component)],
                ['spec.type']: 'Messaging'
            }
        });
        return response.items as MessagingResource[];
    }, [catalogApi]);

    const hasSchedulerConfiguration =
        resources?.some(resource =>
            resource.spec.topology.input?.sources
                .some(output => output.name.includes('scheduler-output'))
        );

    return (<ListItem className={classes.root}>
        <ListItemIcon>{getComponentIcon(type)}</ListItemIcon>
        <ListItemText secondary={description}>
            <Typography variant='body2'>
                {title ? title : name}
                <Chip component='span' label={lifecycle} size='small' className={
                    lifecycle === 'deprecated' ? classes.deprecatedChip : classes.productionChip
                } />
                {hasSchedulerConfiguration && <Chip component='span' label='scheduler' size='small' className={classes.schedulerChip} />}
            </Typography>
        </ListItemText>
        <SonarComponentLinks component={component} />
        <DevopsComponentLinks component={component} />
    </ListItem>);
}

interface ComponentGroups {
    [group: string]: ComponentEntity[];
}

function groupByRelease(components: ComponentEntity[] | undefined): ComponentGroups {

    const groups: ComponentGroups = { all: [] };

    components?.forEach(component => {

        const { labels } = component.metadata;
        const label = (labels || {})['release/group'];

        if (label) {
            if (!groups[label]) {
                groups[label] = [];
            }
            groups[label].push(component);
        } else {
            groups.all.push(component);
        }
    });

    return groups;
}

const GroupedComponentGridItem = (props: { group: string, components: ComponentEntity[] }) => {

    const { group, components } = props;
    const classes = useStyles();

    return (<ul className={classes.ul}>
        <ListSubheader className={classes.groupName}>{group}</ListSubheader>
        {components.map((component, index) => (<ComponentGridItem key={index} component={component} />))}
    </ul>);
}

const NewButton = ({ owner, type }: { owner: string, type: 'component' | 'resource' }) => {
    const navigate = useNavigate();
    return (
        <RequirePermission permission={requestManagerCreatePermission} errorPage={<></>}>
            <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate(`/requests/new-request/${type}`, { state: { owner: owner } })}
            >
                {`New ${type}`}
            </Button>
        </RequirePermission>
    );
};

const SearchInput = ({
                         searchValue,
                         onClear,
                         onChange
}: {
    searchValue: string,
    onClear: React.MouseEventHandler<HTMLButtonElement>,
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
}) => {
    return (
        <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            value={searchValue}
            onChange={onChange}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={onClear}>
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
}

const ResourcesTab = ({ owner }: { owner: string }) => {
    const classes = useStyles();
    const catalogApi = useApi(catalogApiRef);

    const [searchValue, setSearchValue] = useState('');

    const { loading, value: resources = [] } = useAsync(async () => {
        const response = await catalogApi.getEntities({
            filter: {
                kind: 'Resource',
                'spec.system': owner.replace(/^system:/, ''),
                'spec.type': Object.keys(ResourceType),
            },
            fields: ['kind', 'metadata.title', 'metadata.name', 'metadata.description', 'spec.type'],
        });
        return response.items as ResourceEntity[];
    }, []);

    const filteredResources = !searchValue
        ? resources
        : resources.filter(resource => {
            const title = resource.metadata.title?.toLowerCase() || '';
            const name = resource.metadata.name?.toLowerCase() || '';
            return title.includes(searchValue.toLowerCase()) || name.includes(searchValue.toLowerCase());
        });

    return (
        <>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <SearchInput
                        searchValue={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onClear={() => setSearchValue('')}
                    />
                    <RequirePermission permission={requestManagerCreatePermission} errorPage={<></>}>
                        <NewButton owner={owner} type='resource' />
                    </RequirePermission>
                </Box>
                <Divider className={classes.divider} />
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {!filteredResources?.length && (
                        <Box mt={2}>
                            <Typography align="center">No resources found.</Typography>
                        </Box>
                    )}

                    <List dense subheader={<li />}>
                        {filteredResources?.map((resource, index) => (
                            <ResourceGridItem key={index} resource={resource} />
                        ))}
                    </List>
                </>
            )}
        </>
    );
}

export const EntityComponentsCard = (props: {
    components: ComponentEntity[] | undefined;
    owner: string;
}) => {
    const { components, owner } = props;
    const classes = useStyles();
    const [searchValue, setSearchValue] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    const filteredComponents = !searchValue || !components?.length
        ? components
        : components.filter(component => {
            const title = component.metadata.title?.toLowerCase() || '';
            const name = component.metadata.name?.toLowerCase() || '';
            return title.includes(searchValue.toLowerCase()) || name.includes(searchValue.toLowerCase());
        });

    const groups = groupByRelease(filteredComponents);

    return (
        <Card>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} indicatorColor="primary" textColor="primary">
                <Tab label="Components" classes={{root: classes.tab, selected: classes.selectedTab}}/>
                <Tab label="Resources" classes={{root: classes.tab, selected: classes.selectedTab}} />
                <Tab label="Addons" classes={{root: classes.tab, selected: classes.selectedTab}} />
            </Tabs>

            <CardContent>
                {activeTab === 0 && (
                    <>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <SearchInput
                                    searchValue={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onClear={() => setSearchValue('')}
                                />
                                <RequirePermission permission={requestManagerCreatePermission} errorPage={<></>}>
                                    <NewButton owner={owner} type='component' />
                                </RequirePermission>
                            </Box>
                            <Divider className={classes.divider} />

                        {!filteredComponents?.length && (
                            <Box mt={2}>
                                <Typography align='center'>The entity has no components.</Typography>
                            </Box>
                        )}

                        <List dense subheader={<li />}>
                            {Object.keys(groups).length > 1 ? (<>
                                    {[...Object.entries(groups)].map(([group, grouped]) =>
                                        <GroupedComponentGridItem key={group} group={group} components={grouped} />
                                    )}
                                </>
                            ) : (
                                filteredComponents?.map((component, index) => (<ComponentGridItem key={index} component={component} />))
                            )}
                        </List>
                    </>
                )}

                {activeTab === 1 && (
                    <ResourcesTab owner={owner} />
                )}

                {activeTab === 2 && (
                    <AddonsTab owner={owner} />
                )}
            </CardContent>
        </Card>
    );
}
